/**
 * useUserSearch Hook
 *
 * Provides debounced user search functionality for autocomplete.
 * Uses the user repository's searchUsers method.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { RequesterInfo } from '../types/ticket';
import { useUserRepository } from '../data/hooks';

interface UseUserSearchResult {
  /** Current search query */
  query: string;
  /** Set the search query */
  setQuery: (query: string) => void;
  /** Search results */
  results: RequesterInfo[];
  /** Loading state */
  isLoading: boolean;
  /** Error message if search failed */
  error: string | null;
  /** Clear search results and query */
  clear: () => void;
}

interface UseUserSearchOptions {
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number;
  /** Minimum characters before search triggers (default: 2) */
  minChars?: number;
  /** Maximum results to return (default: 10) */
  limit?: number;
}

/**
 * Hook for debounced user search
 *
 * @param options - Configuration options
 * @returns Search state and controls
 */
export function useUserSearch(options: UseUserSearchOptions = {}): UseUserSearchResult {
  const { debounceMs = 300, minChars = 2, limit = 10 } = options;

  const userRepo = useUserRepository();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<RequesterInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track the latest search to avoid race conditions
  const latestSearch = useRef<string>('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Perform the actual search
  const performSearch = useCallback(
    async (searchQuery: string) => {
      // Track this search
      latestSearch.current = searchQuery;

      // Skip if below minimum characters
      if (searchQuery.length < minChars) {
        setResults([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const searchResults = await userRepo.searchUsers(searchQuery, limit);

        // Only update if this is still the latest search
        if (latestSearch.current === searchQuery) {
          setResults(searchResults);
          setIsLoading(false);
        }
      } catch (err) {
        // Only update if this is still the latest search
        if (latestSearch.current === searchQuery) {
          console.error('User search failed:', err);
          setError('Failed to search users. Please try again.');
          setResults([]);
          setIsLoading(false);
        }
      }
    },
    [userRepo, minChars, limit]
  );

  // Handle query changes with debouncing
  useEffect(() => {
    // Clear any existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Clear results if query is too short
    if (query.length < minChars) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    // Set loading immediately for better UX
    setIsLoading(true);

    // Debounce the search
    debounceTimer.current = setTimeout(() => {
      performSearch(query);
    }, debounceMs);

    // Cleanup
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [query, debounceMs, minChars, performSearch]);

  // Clear function
  const clear = useCallback(() => {
    setQuery('');
    setResults([]);
    setError(null);
    setIsLoading(false);
    latestSearch.current = '';
  }, []);

  return {
    query,
    setQuery,
    results,
    isLoading,
    error,
    clear,
  };
}
